import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import React from "react";
import { useRouter } from "next/router";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { clearRouterPath, hasItems, takeFirst } from "~/features/app/utils";
import { type PartnerCardFragment, type PartnersFiltersQuery } from "~/gql/graphql";
import { LoadMore } from "~/features/ui/load-more";
import { PartnersFilters } from "~/features/blocks/inventory/partners/partners-filters";
import { PartnerCard } from "~/features/card/partner-card";
import { NoResults } from "~/features/filter/no-results";
import { useInfiniteScroll } from "~/features/ui/use-infinite-scroll";
const LIMIT = 9;
type Anchor = {
  id?: string | null;
  name?: string | null;
};
export type PartnersBlockViewProps = {
  anchor?: Anchor | Anchor[] | null;
  filters?: PartnersFiltersQuery | null;
  hasTopDivider?: boolean | null;
  initialPartners?: PartnerCardFragment[] | null;
  mode?: string | null;
  theme?: string | null;
  total?: number;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & Record<`data-${string}`, string | undefined>;
};

// `initialPartners` carries an extra `industrySlug` (not rendered by the card)
// so the listing can filter by industry client-side, mirroring the server-side
// filtering the removed `/api/get-partners` route did.
type PartnerCardWithIndustry = PartnerCardFragment & {
  industrySlug?: string | null;
};
export function PartnersBlockView(props: PartnersBlockViewProps) {
  const {
    mode = "light",
    theme = "white",
    anchor,
    hasTopDivider = true,
    filters,
    initialPartners = [],
    sectionProps
  } = props;
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const router = useRouter();
  const activeRegion = router.query.region as string || "";
  const activeService = router.query.service as string || "";
  const activeIndustry = router.query.industry as string || "";

  // Partners are loaded server-side and passed as props; filtering and
  // pagination run client-side from `router.query`.
  const filteredPartners = React.useMemo(() => {
    return ((initialPartners ?? []) as PartnerCardWithIndustry[]).filter(partner => {
      const regionMatches = !activeRegion || (partner.regions ?? []).some(region => region?.slug === activeRegion);
      const serviceMatches = !activeService || (partner.services ?? []).some(service => service?.slug === activeService);
      const industryMatches = !activeIndustry || partner.industrySlug === activeIndustry;
      return regionMatches && serviceMatches && industryMatches;
    });
  }, [initialPartners, activeRegion, activeService, activeIndustry]);
  const [visibleCount, setVisibleCount] = React.useState(LIMIT);
  React.useEffect(() => {
    setVisibleCount(LIMIT);
  }, [activeRegion, activeService, activeIndustry]);
  const partners = React.useMemo(() => filteredPartners.slice(0, visibleCount), [filteredPartners, visibleCount]);
  const isLoadingMore = false;
  const hasMore = visibleCount < filteredPartners.length;
  function onChange(filterType: string, e: string) {
    const queryParams = {
      ...router.query
    };
    if (filterType === "Region") {
      queryParams.region = e;
    }
    if (filterType === "Service") {
      queryParams.service = e;
    }
    if (filterType === "Industry") {
      queryParams.industry = e;
    }
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: queryParams
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }
  function onClearFilters() {
    const {
      region,
      service,
      industry,
      ...rest
    } = router.query;
    router.push({
      pathname: clearRouterPath(router.asPath),
      query: rest
    }, undefined, {
      shallow: true,
      scroll: false
    });
  }
  const loadMoreRef = useInfiniteScroll({
    hasMore,
    isLoading: isLoadingMore,
    onLoadMore: () => setVisibleCount(prev => prev + LIMIT)
  });
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, themeStyle.border, themeStyle.background]}>
      <PartnersFilters mode={mode as Mode} theme={theme as Theme} {...filters} onChange={onChange} value={{
      region: activeRegion,
      service: activeService,
      industry: activeIndustry
    }} />
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledGrid $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "gap": "0.1rem",
          "@media (min-width: 1024px)": {
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }
        }, themeStyle.background, themeStyle.border]}>
            {!hasItems(partners) ? <_StyledDiv2 $_css3={[{
            "gridColumn": "1 / -1",
            "borderTopWidth": "1px",
            "paddingLeft": "4rem",
            "paddingRight": "4rem",
            "paddingTop": "4rem",
            "paddingBottom": "4rem"
          }, themeStyle.border]}>
                <NoResults onClear={onClearFilters} />
              </_StyledDiv2> : partners.map((partner, idx) => <_StyledDiv3 key={idx} $_css4={[{
            "gridColumn": "span 4 / span 4",
            "outlineStyle": "solid",
            "outlineWidth": "1px"
          }, themeStyle.outline]}>
                  <PartnerCard theme={theme as Theme} mode={mode as Mode} {...partner} />
                </_StyledDiv3>)}
          </_StyledGrid>
        </_StyledDiv>
        {hasMore && <div ref={loadMoreRef}>
            <LoadMore isLoading={isLoadingMore} onClick={() => setVisibleCount(prev => prev + LIMIT)} />
          </div>}
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-eers6u-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-eers6u-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-eers6u-2"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-eers6u-3"
})`${p => p.$_css3}`;
var _StyledDiv3 = /*#__PURE__*/_styled5("div").withConfig({
  displayName: "partners___StyledDiv3",
  componentId: "sc-1rlm9qn-0"
})(["", ""], p => p.$_css4);